import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SimpleHeader from '../components/SimpleHeader';
import Footer from '../components/Footer';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { BookOpen, Check } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { createCheckoutSession, getCustomerPurchases } from '../services/paymentService';

const AdvancedGuidePage = () => {
  const [email, setEmail] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const features = [
    'Solar panel ROI calculator & setup guide',
    'Rainwater harvesting systems (by climate zone)',
    'Seasonal growing calendars & crop planning',
    'Composting systems from kitchen to garden',
    'DIY energy audit walkthrough',
    'Home insulation & weatherization projects',
    'Greywater recycling for irrigation',
    'Food preservation & fermentation basics'
  ];

  const handlePurchase = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      const emailToUse = email || null;
      const { checkout_url } = await createCheckoutSession('advanced_guide', emailToUse);
      window.location.href = checkout_url;
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast({ title: 'Error', description: 'Failed to create checkout session. Please try again.', variant: 'destructive' });
      setIsProcessing(false);
    }
  };

  const handleUnlock = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast({ title: 'Invalid Email', description: 'Please enter a valid email address.', variant: 'destructive' });
      return;
    }
    try {
      const { purchases } = await getCustomerPurchases(email);
      if (purchases.length > 0) {
        toast({ title: 'Access Granted!', description: `Found ${purchases.length} purchase(s). Redirecting...`, variant: 'default' });
        setTimeout(() => { window.location.href = '/access'; }, 1500);
      } else {
        toast({ title: 'No Purchases Found', description: "We couldn't find any purchases with this email.", variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to verify purchases.', variant: 'destructive' });
    }
  };

  return (
    <>
      <div className="min-h-screen bg-[#F5F1E8]">
        <SimpleHeader />
        
        <main className="pt-24 pb-20 px-6">
          <div className="max-w-3xl mx-auto">
            {/* Branded Banner */}
            <div className="rounded-xl overflow-hidden shadow-xl mb-10">
              <img src="/images/spendless-banner.png" alt="Spend Less. Live More." className="w-full h-auto" data-testid="product-banner-img" />
            </div>
            
            <div className="text-center">
              <div className="flex items-center justify-center gap-3 mb-4">
                <img src="/images/spendless-logo.png" alt="SpendLess" className="w-14 h-14 rounded-full shadow-md" data-testid="product-logo-img" />
                <p className="text-[#6B7A5C] text-sm uppercase tracking-wider font-semibold">Premium Content</p>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold text-[#2C1810] mb-4" data-testid="advanced-guide-title">
                Advanced Systems Guide
              </h1>
              
              <p className="text-[#5A5046] text-xl mb-12">
                Solar, rainwater, seasonal growing, composting, and DIY energy audits — the technical deep-dive
              </p>
              
              <Card className="bg-white border-2 border-[#E5DFD0] shadow-xl p-12 max-w-xl mx-auto">
                <div className="flex justify-center mb-6">
                  <div className="w-20 h-20 rounded-full bg-[#6B7A5C] bg-opacity-10 flex items-center justify-center">
                    <BookOpen className="w-10 h-10 text-[#6B7A5C]" />
                  </div>
                </div>
                
                {/* Price */}
                <div className="text-4xl font-bold text-[#C17855] mb-2">$29.99</div>
                <p className="text-[#5A5046] text-sm mb-6">One-time payment. Lifetime access.</p>

                {/* Features */}
                <div className="text-left mb-6">
                  <h3 className="text-lg font-bold text-[#2C1810] mb-3">What's inside:</h3>
                  <div className="space-y-2">
                    {features.map((feature, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-[#6B7A5C] mt-0.5 flex-shrink-0" />
                        <span className="text-[#5A5046] text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <Button 
                  onClick={handlePurchase}
                  disabled={isProcessing}
                  className="w-full bg-[#C17855] hover:bg-[#B06745] text-white px-8 py-6 text-lg rounded-lg transition-all shadow-md hover:shadow-lg mb-8"
                  data-testid="advanced-guide-purchase-btn"
                >
                  {isProcessing ? 'Redirecting to checkout...' : 'Purchase — $29.99'}
                </Button>
                
                <div className="border-t border-[#E5DFD0] pt-8">
                  <p className="text-[#2C1810] font-semibold mb-4">Already purchased?</p>
                  <form onSubmit={handleUnlock} className="space-y-4">
                    <p className="text-sm text-[#5A5046] mb-4">Enter your email to unlock your content:</p>
                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" className="w-full px-6 py-3 rounded-lg border-[#E5DFD0]" data-testid="advanced-guide-email-input" />
                    <Button type="submit" variant="outline" className="w-full border-2 border-[#6B7A5C] text-[#6B7A5C] hover:bg-[#6B7A5C] hover:text-white px-6 py-3 rounded-lg transition-all" data-testid="advanced-guide-unlock-btn">Unlock</Button>
                  </form>
                </div>
              </Card>
            </div>
          </div>
        </main>
        
        <Footer />
      </div>
      <Toaster />
    </>
  );
};

export default AdvancedGuidePage;
